import React, { useEffect, useRef, useState } from 'react'
import useOnClickOutside from 'hooks/use-click-outside-hooks'
import styled from 'styled-components'

import Box from 'components/Box'
import Icon from 'components/Icon'
import AddonIcon from 'components/TextInput/AddonIcon'
import TextInput from 'components/TextInput'

import { LeadListModal } from 'modules/Leads/shared/CustomLeadFormFields/FieldTypes/LookupField/LeadListModal'
import { LeadInfo } from 'store/types'

import { FieldTypeProps } from './../../FieldTypes'
import { getLeadRoute } from 'utils/url'
import { Flex, Text, Tooltip } from 'components'
import { colors, HexToRgba } from 'utils/colors'
import { noop } from 'lodash-es'
import InlineEditBtn from 'modules/Leads/LeadDetailV2/shared/InlineEditBtn'
import { useReusableTab } from 'hooks/use-reusable-lead-tab'

export type LookupFieldValue = {
  leadId: string
  leadName: string
}

const DEFAULT_LEAD_VALUE: LookupFieldValue = {
  leadId: '',
  leadName: '',
}

export const LookupField: React.FC<FieldTypeProps> = ({
  isLeftPanel = false,
  accessor,
  label,
  value,
  onChange,
  ...restProps
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { openInReusableTab } = useReusableTab()
  const [displayInlineEditBtn, setDisplayInlineEditBtns] = useState(false)
  const [fieldValue, setFieldValue] = useState(DEFAULT_LEAD_VALUE)
  const [showLeadListModal, setShowLeadListModal] = useState(false)

  const [displayEditBtn, setDisplayEditBtn] = useState(false)

  const isLeadReadOnly = restProps?.readOnly

  useOnClickOutside(ref, () => {
    displayInlineEditBtn && setDisplayInlineEditBtns(false)
  })

  useEffect(() => {
    try {
      if (value && typeof value !== 'string') {
        if (Object.keys(value).includes('leadId') && Object.keys(value).includes('leadName')) {
          setFieldValue(value)
          return
        }

        throw new Error('Invalid value', value)
      }

      const data = JSON.parse(value || '{}')
      if (data && Object.keys(data).includes('leadId') && Object.keys(data).includes('leadName')) {
        setFieldValue(data)
        return
      }
      setFieldValue(DEFAULT_LEAD_VALUE)
    } catch (e) {
      console.error(e)
      setFieldValue(DEFAULT_LEAD_VALUE)
    }
  }, [value])

  const onEditBtnClick = () => {
    setDisplayEditBtn(false)
    if (!fieldValue.leadId) {
      setShowLeadListModal(true)
    } else {
      setDisplayInlineEditBtns(true)
    }
  }

  const onClickEdit = () => {
    setDisplayEditBtn(false)
    setShowLeadListModal(true)
  }

  const handleLeadListModalSubmit = (lead?: LeadInfo) => {
    const newFieldValue: LookupFieldValue = {
      leadId: lead?.leadId || '',
      leadName: lead?.leadName || '',
    }

    if (isLeftPanel) {
      setDisplayInlineEditBtns(true)
    } else {
      onChange?.(newFieldValue)
    }
    setFieldValue(newFieldValue)
  }

  const handleLeadListModalClose = () => {
    setShowLeadListModal(false)
  }

  const handleLinkClick = () => {
    if (fieldValue.leadId) {
      openInReusableTab(getLeadRoute({ leadId: fieldValue.leadId }))
    }
  }

  const handleClearClick = () => {
    if (!isLeftPanel) {
      onChange?.(DEFAULT_LEAD_VALUE)
    }
    setFieldValue(DEFAULT_LEAD_VALUE)
  }

  const renderListModal = () => (
    <LeadListModal
      accessor={accessor}
      isVisible={showLeadListModal}
      onSubmit={handleLeadListModalSubmit}
      onClose={handleLeadListModalClose}
    />
  )

  const isDisabled = isLeadReadOnly || restProps.isDisabled

  if (isLeftPanel && !displayInlineEditBtn) {
    const additionalProps = {
      onMouseEnter: () => {
        !isDisabled && setDisplayEditBtn(true)
      },
      onMouseLeave: () => {
        !isDisabled && setDisplayEditBtn(false)
      },
    }

    return (
      <>
        <FieldBoxWrapper disabled={isDisabled}>
          <Flex
            gap="0"
            justifyContent="space-between"
            borderBottom={`1px solid ${colors.gray3}`}
            position="relative"
            {...(!fieldValue.leadName ? additionalProps : null)}
          >
            <Flex
              gap="4px"
              justifyContent="space-between"
              p={`6px ${!fieldValue.leadName ? '0px' : '16px'} 6px 16px`}
              width="100%"
              minWidth={0}
              {...(fieldValue.leadName ? additionalProps : null)}
            >
              <Text lineHeight="20px" textAlign="left">
                {label}
              </Text>
              <Text fontWeight="medium" textAlign="right" minWidth={0} wordBreak="break-all">
                {fieldValue.leadName}
              </Text>
              {displayEditBtn && (
                <Box
                  position="absolute"
                  top="50%"
                  transform="translateY(-50%)"
                  right={!fieldValue.leadName ? '0px' : '32px'}
                  onClick={onEditBtnClick}
                >
                  <InlineEditBtn />
                </Box>
              )}
            </Flex>
            {fieldValue.leadName && (
              <Tooltip title="open_in_new_tab" position="bottomRight">
                <HoverStyles
                  borderLeft={`1px solid ${colors.gray3}`}
                  cursor="pointer"
                  onClick={!isDisabled ? handleLinkClick : noop}
                >
                  <Icon fontSize="16px" icon={['fa', 'link']}></Icon>
                </HoverStyles>
              </Tooltip>
            )}
          </Flex>
        </FieldBoxWrapper>
        {showLeadListModal && renderListModal()}
      </>
    )
  }

  const lookupField = (
    <TextInput
      {...restProps}
      label={label}
      type="text"
      value={fieldValue.leadName}
      variant="labeled"
      disabled={isDisabled}
      onFocus={onClickEdit}
      height={isLeftPanel ? '40px' : '48px'}
      placeholder="lead_lookup_placeholder_lbl"
      addonBefore={<AddonIcon position="before" icon={['far', 'magnifying-glass']} />}
      addonAfter={
        <>
          {fieldValue.leadName && !isDisabled && (
            <Flex className="box delete" onClick={handleClearClick} gap="0px">
              <Icon fontSize={16} icon={['fa', 'times']} color="white" />
            </Flex>
          )}
          <AddonIcon
            disabled={!fieldValue.leadId}
            position="after"
            icon={['fa', 'link']}
            onClick={handleLinkClick}
            {...(fieldValue.leadId && {
              bg: 'blue1',
              color: 'white',
            })}
          />
        </>
      }
    />
  )

  return (
    <Styles isLeftPanel={isLeftPanel} ref={ref}>
      {!isLeftPanel ? (
        lookupField
      ) : (
        <Flex width="100%" p="8px 16px" alignItems="flex-end" gap="8px">
          {lookupField}
          <InlineButtons
            onOkClick={() => {
              setDisplayInlineEditBtns(false)
              onChange?.(fieldValue)
            }}
            onCancelClick={() => {
              setDisplayInlineEditBtns(false)
              const data = JSON.parse(value || '{}')
              if (data.leadId) {
                setFieldValue(data)
                return
              }
              setFieldValue(DEFAULT_LEAD_VALUE)
            }}
          />
        </Flex>
      )}
      {showLeadListModal && renderListModal()}
    </Styles>
  )
}

const FieldBoxWrapper = styled(Box)<{ disabled?: boolean }>`
  *:hover {
    ${({ theme, disabled }) => !disabled && `background: ${theme.colors.gray1}`};
  }
`

const Styles = styled.div<{ isLeftPanel: boolean }>`
  .box {
    &.delete {
      position: absolute;
      top: ${({ isLeftPanel }) => (isLeftPanel ? '50%' : '14px')};
      transform: ${({ isLeftPanel }) => (isLeftPanel ? 'translateY(-50%)' : 'none')};
      right: 60px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${(p) => p.theme.colors.gray4};

      :hover {
        cursor: pointer;
        background-color: ${(p) => p.theme.colors.blue1};

        svg {
          color: ${(p) => p.theme.colors.white};
        }
      }
    }
  }
`
const HoverStyles = styled(Flex)`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    border: 1px solid ${colors.blue1};
    background: ${HexToRgba(colors.blue5, 0.7)};
  }
`

export default LookupField

type InlineBtnProps = {
  onOkClick: () => void
  onCancelClick: () => void
}

const InlineButtons = ({ onOkClick, onCancelClick }: InlineBtnProps) => (
  <Flex gap="4px">
    <Flex
      padding="12px"
      backgroundColor={HexToRgba(colors.green3, 0.15)}
      border={`1px solid ${colors.green3}`}
      borderRadius="4px"
      onClick={onOkClick}
      cursor="pointer"
    >
      <Icon icon={['fas', 'check']} color={colors.green3} />
    </Flex>
    <Flex
      padding="12px"
      border={`1px solid ${colors.red}`}
      borderRadius="4px"
      backgroundColor={HexToRgba(colors.red, 0.15)}
      cursor="pointer"
      onClick={onCancelClick}
    >
      <Icon icon={['fas', 'times']} color={colors.red} />
    </Flex>
  </Flex>
)
